import React, { useMemo, useState } from 'react';
import RestService from '../../services/restService';
import { useDispatch } from 'react-redux';
import { useToasts } from 'react-toast-notifications';
import { logout } from '../../redux/actions/auth';
import PasswordField2 from '../../components/common/PasswordField/PasswordField2';
import Button from '../../components/common/Button';
import { useNavigate } from 'react-router-dom';

const ResetPassword = ({ staffOrAgent, userType }) => {
  const { addToast } = useToasts();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [oldPassword, setOldPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const containsLowerCase = (str) => /[a-z]/.test(str);
  const containsUpperCase = (str) => /[A-Z]/.test(str);
  const containsDigit = (str) => /\d/.test(str);

  const rules = useMemo(() => {
    const hasLength = password.length > 7;
    const hasUpper = containsUpperCase(password);
    const hasLower = containsLowerCase(password);
    const hasDigit = containsDigit(password);
    const matches = password && password === confirmPassword;
    const hasTemp = Boolean(oldPassword);
    return { hasLength, hasUpper, hasLower, hasDigit, matches, hasTemp };
  }, [password, confirmPassword, oldPassword]);

  const allValid = Object.values(rules).every(Boolean);

  const updatePassword = async (e) => {
    e.preventDefault();
    setError('');
    if (!allValid) return;

    setLoading(true);
    try {
      if (userType === 'staff') {
        await RestService.StaffApi.changePassword(staffOrAgent?.id, oldPassword, password, confirmPassword);
        addToast('Successfully changed password. Please log in.', {
          appearance: 'success',
          autoDismiss: true,
          autoDismissTimeout: 3000,
        });
        setTimeout(() => {
          dispatch(logout());
          navigate('/staff', { replace: true });
        }, 1500);
      } else {
        await RestService.AgentsApi.changePassword(staffOrAgent?.id, oldPassword, password, confirmPassword);
        addToast('Successfully changed password. Please log in.', {
          appearance: 'success',
          autoDismiss: true,
          autoDismissTimeout: 3000,
        });
        setTimeout(() => {
          dispatch(logout());
          navigate('/', { replace: true });
        }, 1500);
      }
    } catch (err) {
      setError(err?.response?.data?.message || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <form className="w-full px-4 lg:px-0 mx-auto flex flex-col gap-6">
        <PasswordField2
          labelName="Temporary Password"
          onChange={(e) => setOldPassword(e.target.value)}
          placeholder="Enter temporary password"
          name="oldPassword"
        />
        <PasswordField2
          labelName="New Password"
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Enter new password"
          name="password"
        />
        <PasswordField2
          labelName="Confirm Password"
          onChange={(e) => setConfirmPassword(e.target.value)}
          placeholder="Confirm new password"
          name="confirmPassword"
          error={error}
        />

        <div className="w-full flex flex-col gap-3">
          <Button onClick={updatePassword} loading={loading} disabled={loading || !allValid} className="w-full" size="lg">
            Reset Password
          </Button>
          {error ? (
            <div className="flex items-center gap-3 p-3 bg-red-50 border border-red-200 rounded-lg" aria-live="polite">
              <svg className="w-4 h-4 text-red-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <span className="text-sm text-red-800">{error}</span>
            </div>
          ) : null}
        </div>

        <div>
          <ul className="max-w-md space-y-2 text-gray-500 list-inside">
            <li className="flex items-center">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className={`feather feather-check-circle ${rules.hasLength ? 'text-green-500' : ''}`}
              >
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                <polyline points="22 4 12 14.01 9 11.01"></polyline>
              </svg>
              <span className={`ml-3 ${rules.hasLength ? 'text-green-500' : ''}`}>At least 8 characters</span>
            </li>
            <li className="flex items-center">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`feather feather-check-circle ${rules.hasUpper ? 'text-green-500' : ''}`}></svg>
              <span className={`ml-3 ${rules.hasUpper ? 'text-green-500' : ''}`}>At least 1 uppercase letter</span>
            </li>
            <li className="flex items-center">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`feather feather-check-circle ${rules.hasLower ? 'text-green-500' : ''}`}></svg>
              <span className={`ml-3 ${rules.hasLower ? 'text-green-500' : ''}`}>At least 1 lowercase letter</span>
            </li>
            <li className="flex items-center">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`feather feather-check-circle ${rules.hasDigit ? 'text-green-500' : ''}`}></svg>
              <span className={`ml-3 ${rules.hasDigit ? 'text-green-500' : ''}`}>At least 1 number</span>
            </li>
            <li className="flex items-center">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`feather feather-check-circle ${rules.matches ? 'text-green-500' : ''}`}></svg>
              <span className={`ml-3 ${rules.matches ? 'text-green-500' : ''}`}>Passwords must match</span>
            </li>
            <li className="flex items-center">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`feather feather-check-circle ${rules.hasTemp ? 'text-green-500' : ''}`}></svg>
              <span className={`ml-3 ${rules.hasTemp ? 'text-green-500' : ''}`}>Input temporary password</span>
            </li>
          </ul>
        </div>
      </form>
    </>
  );
};

export default ResetPassword;